// Rozbalovací sekce detailu produktu na telefonu. Na počítači se záložky přepínají bez skriptu (CSS a skryté
// přepínače); tady jen nadpis panelu otevírá a zavírá jeho obsah. Třída is-js zapne skrývání zavřených sekcí,
// takže bez skriptu zůstane obsah vidět.
//
// Související produkty a alternativy nejsou v prvním HTML detailu (karta má ~48 kB, BEST-400): panel je načte ze své
// sekce přes Section Rendering API na adrese produktu jednou a další otevření už nic nenačítá. Aby otevření ukázalo
// karty hned, načtou se dopředu, když se zákazník k záložkám blíží (asi obrazovku předem, ve volné chvíli prohlížeče)
// nebo na záložku najede, klepne či na ni přejde klávesnicí; se zapnutým šetřením dat (Save-Data) až při otevření.
// Sekce vrátí nejvýš 6 karet; „Zobrazit další“ přidá dalších 6 ze sekce karty na adrese každého produktu (BEST-491).
(() => {
  /** The customer asked the browser to save data: a panel then loads only when it opens. */
  const savesData = () => Boolean(window.navigator?.connection?.saveData);

  /** Runs the callback when the browser is idle (Safari has no requestIdleCallback). */
  const whenIdle = (callback) => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(callback, {timeout: 2000});
    else window.setTimeout(callback, 200);
  };

  /**
   * The first image of a card is lazy in the section, and a lazy image in a closed panel waits until the panel opens and
   * then appears late. The first page of a tab (at most 6 cards) loads its images together with the products; the
   * image wrapper of the card keeps their space, so nothing moves when they arrive. Other images of a card stay lazy.
   */
  const loadFirstCardImages = (list) => {
    list.querySelectorAll('.product-card__image-wrapper').forEach((wrapper) => {
      wrapper.querySelector('img')?.setAttribute('loading', 'eager');
    });
  };

  /**
   * Loads the products of a lazy panel once. `moveFocus` is set by the retry button: that button disappears while the
   * products load, so the focus goes to the loaded list, or back to the button when the load fails again. `prefetch`
   * loads a panel nobody opened yet: only once, and its failure shows nothing, because opening the panel loads again.
   */
  const loadPanelProducts = async (container, {moveFocus = false, prefetch = false} = {}) => {
    const state = container.dataset.bestioTabsLazyState;
    if (prefetch ? Boolean(state) : state === 'loading' || state === 'loaded') return;
    const status = container.querySelector('[data-bestio-tabs-lazy-status]');
    const error = container.querySelector('[data-bestio-tabs-lazy-error]');
    const skeleton = container.querySelector('[data-bestio-tabs-lazy-skeleton]');
    container.dataset.bestioTabsLazyState = 'loading';
    container.setAttribute('aria-busy', 'true');
    if (status) status.hidden = false;
    if (skeleton) skeleton.hidden = false;
    if (error) error.hidden = true;
    // The retry button is inside the error that hides now; the panel (busy, with its placeholder cards) keeps the
    // focus meanwhile.
    if (moveFocus) {
      container.setAttribute('tabindex', '-1');
      container.focus({preventScroll: true});
    }
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', container.dataset.bestioTabsLazy);
      const response = await fetch(url.toString(), {credentials: 'same-origin'});
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = html.querySelector('[data-bestio-tabs-products-list]');
      if (!list) throw new Error('The section has no product list.');
      loadFirstCardImages(list);
      container.innerHTML = list.innerHTML;
      container.dataset.bestioTabsLazyState = 'loaded';
      // The panel kept the focus while it loaded; its content changed silently, so the first product takes the focus
      // and a screen reader reads it (as after "Show more").
      if (moveFocus) container.querySelector('a[href]:not([tabindex="-1"])')?.focus({preventScroll: true});
      if (typeof window.initializeScrollAnimationTrigger === 'function') window.initializeScrollAnimationTrigger();
    } catch (failure) {
      console.error('Bestio product tabs: products could not be loaded.', container.dataset.bestioTabsLazy, failure);
      container.dataset.bestioTabsLazyState = 'failed';
      // A panel nobody opened keeps its placeholder cards: the error would be for nobody, and opening loads again.
      if (container.dataset.bestioTabsLazyOpened) {
        if (status) status.hidden = true;
        if (skeleton) skeleton.hidden = true;
        if (error) error.hidden = false;
        if (moveFocus) container.querySelector('[data-bestio-tabs-lazy-retry]')?.focus({preventScroll: true});
      }
    } finally {
      container.removeAttribute('aria-busy');
    }
  };

  // A tab shows 6 cards at a time (BEST-491): the section of the tab renders the first 6 and lists the addresses of the
  // others on its "Show more" button; each next card comes from the card section on the address of its own product.
  const PAGE_SIZE = 6;

  /**
   * The card of one product from the card section of its tab, '' for a product that is no longer available or no
   * longer exists (404, nothing to try again); throws when the card cannot be loaded.
   */
  const loadCard = async (productUrl, cardSection) => {
    const url = new URL(productUrl, window.location.origin);
    if (url.origin !== window.location.origin) return '';
    url.searchParams.set('section_id', cardSection);
    const response = await fetch(url.toString(), {credentials: 'same-origin'});
    if (response.status === 404) return '';
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const card = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-bestio-tabs-product-card]');
    return card ? card.innerHTML : '';
  };

  /**
   * Adds the next 6 cards under the loaded ones. A card that fails stays first in line, so the same button loads it
   * again; the button disappears once nothing is left. The focus moves to the first new card, because the button the
   * customer pressed is now below it or gone.
   */
  const loadMoreProducts = async (button) => {
    if (button.dataset.bestioTabsMoreState === 'loading') return;
    const panel = button.closest('[data-bestio-tabs-lazy]');
    const list = panel?.querySelector('[data-bestio-tabs-products]');
    if (!list) return;
    const error = panel.querySelector('[data-bestio-tabs-more-error]');
    // One live region next to the button, always in the page and empty until a load ends; it says how the load ended.
    const status = panel.querySelector('[data-bestio-tabs-more-status]');
    const pending = (button.dataset.bestioTabsMore || '').split(/\s+/).filter(Boolean);
    const page = pending.slice(0, PAGE_SIZE);
    const hadFocus = document.activeElement === button;
    button.dataset.bestioTabsMoreState = 'loading';
    button.disabled = true;
    list.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;
    if (status) status.textContent = '';
    const results = await Promise.all(page.map(async (productUrl) => {
      try {
        return {productUrl, html: await loadCard(productUrl, button.dataset.bestioTabsMoreSection)};
      } catch (failure) {
        console.error('Bestio product tabs: a product card could not be loaded.', productUrl, failure);
        return {productUrl, failed: true};
      }
    }));
    const before = list.children.length;
    results.filter((result) => !result.failed && result.html).forEach((result) => list.insertAdjacentHTML('beforeend', result.html));
    const failed = results.filter((result) => result.failed).map((result) => result.productUrl);
    const remaining = [...failed, ...pending.slice(PAGE_SIZE)];
    list.removeAttribute('aria-busy');
    button.dataset.bestioTabsMore = remaining.join(' ');
    button.dataset.bestioTabsMoreState = failed.length ? 'failed' : 'loaded';
    button.disabled = false;
    if (error) error.hidden = failed.length === 0;
    if (status) status.textContent = (failed.length ? status.dataset.failedText : status.dataset.loadedText) || '';
    if (!remaining.length) button.hidden = true;
    const firstNew = list.children[before];
    if (hadFocus && firstNew) firstNew.querySelector('a[href]:not([tabindex="-1"])')?.focus();
    else if (hadFocus && button.hidden) list.querySelector('a[href]:not([tabindex="-1"])')?.focus();
    // A disabled button loses the focus; when nothing new came, it gets it back for another try.
    else if (hadFocus) button.focus();
    if (typeof window.initializeScrollAnimationTrigger === 'function') window.initializeScrollAnimationTrigger();
  };

  /** The lazy product list in the panel body with this id, null for a panel with its content in the first HTML. */
  const lazyPanel = (bodyId) => (bodyId ? document.getElementById(bodyId)?.querySelector('[data-bestio-tabs-lazy]') : null) || null;

  /** Loads the products of the panel whose body has this id, when the panel opens. */
  const loadPanel = (bodyId) => {
    const container = lazyPanel(bodyId);
    if (!container) return;
    // An open panel shows the failure of a load that is still running (a prefetch) and loads again after one.
    container.dataset.bestioTabsLazyOpened = '1';
    loadPanelProducts(container);
  };

  /** Starts loading a panel before it opens, unless the customer saves data. */
  const prefetchPanel = (container) => {
    if (container && !savesData()) loadPanelProducts(container, {prefetch: true});
  };

  /**
   * The tab (label of the desktop radio, the radio itself) or the heading button of the phone accordion that the
   * customer is about to use: its panel loads right away, the click comes a moment later.
   */
  const PANEL_TRIGGER = '[data-bestio-tabs-toggle], .bestio-tabs__tab[for], .bestio-tabs__input';
  const prefetchOnIntent = (event) => {
    const trigger = event.target?.closest?.(PANEL_TRIGGER);
    if (!trigger) return;
    const bodyId = trigger.getAttribute('aria-controls') || `${trigger.getAttribute('for') || trigger.id}-body`;
    prefetchPanel(lazyPanel(bodyId));
  };

  /** Loads all lazy panels of the tabs in an idle moment once the tabs are about one screen away. */
  const prefetchOnApproach = (root) => {
    if (typeof window.IntersectionObserver !== 'function' || savesData()) return;
    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      whenIdle(() => root.querySelectorAll('[data-bestio-tabs-lazy]').forEach(prefetchPanel));
    }, {rootMargin: '100% 0px'});
    observer.observe(root);
  };

  const init = (root) => {
    if (root.dataset.bestioTabsReady) return;
    root.dataset.bestioTabsReady = '1';
    root.classList.add('is-js');
    root.querySelectorAll('[data-bestio-tabs-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const panel = toggle.closest('.bestio-tabs__panel');
        if (!panel) return;
        const open = !panel.classList.contains('is-open');
        panel.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        if (open) loadPanel(toggle.getAttribute('aria-controls'));
      });
    });
    // Desktop tabs are hidden radio inputs; the body of a tab's panel has the input's id with "-body".
    root.querySelectorAll('.bestio-tabs__input').forEach((input) => {
      input.addEventListener('change', () => { if (input.checked) loadPanel(`${input.id}-body`); });
      // The browser restores a checked tab after going back; its panel is visible straight away.
      if (input.checked) loadPanel(`${input.id}-body`);
    });
    ['pointerdown', 'focusin', 'mouseover'].forEach((type) => root.addEventListener(type, prefetchOnIntent, {passive: true}));
    prefetchOnApproach(root);
    root.addEventListener('click', (event) => {
      const more = event.target.closest('[data-bestio-tabs-more]');
      if (more) {
        loadMoreProducts(more);
        return;
      }
      const retry = event.target.closest('[data-bestio-tabs-lazy-retry]');
      const container = retry?.closest('[data-bestio-tabs-lazy]');
      if (container) loadPanelProducts(container, {moveFocus: true});
    });
  };
  /**
   * "No reviews yet" under the product title (BEST-612) opens the reviews, "Show all alternatives (N)" in the
   * alternatives on the detail (BEST-627) the alternatives: the desktop tab (its radio) or the phone section, then
   * scrolls to it. Opening loads a lazy panel (alternatives) as a click on its tab does. Without the script the link still
   * jumps to the panel.
   */
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-bestio-open-reviews], [data-bestio-open-tab]');
    if (!link) return;
    const tabsId = link.dataset.bestioOpenTab || link.dataset.bestioOpenReviews;
    const name = link.dataset.bestioOpenPanel || 'reviews';
    const root = document.getElementById(tabsId);
    const panel = document.getElementById(`${tabsId}-${name}-panel`);
    if (!root || !panel) return;
    event.preventDefault();
    const input = document.getElementById(`${tabsId}-${name}`);
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', {bubbles: true}));
    }
    const toggle = panel.querySelector('[data-bestio-tabs-toggle]');
    if (toggle && !panel.classList.contains('is-open')) toggle.click();
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // A phone shows the open section, a computer the whole tab bar with the panel under it.
    const target = window.matchMedia?.('(max-width: 767px)').matches ? panel : root;
    target.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
  });

  const run = () => document.querySelectorAll('[data-bestio-product-tabs]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
